import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import type {
  PageForm,
  PageFormAction,
} from "@/pages/tina/festivals/lib/page-form";
import type { FestivalPageRead, RereadRead } from "@/types/tina-festival-page";

dayjs.extend(relativeTime);

/** How Tina's re-reads of a page stand, in a few words; nothing before its first one. */
export function rereadText(reread: RereadRead | null): string | null {
  if (reread === null) return null;
  if (reread.state === "dead")
    return "Tina stopped reading it: the page is gone";
  if (reread.last_outcome === "cancelled") return "says it is cancelled";
  if (reread.state === "active" && reread.last_read_at !== null) {
    return `checked ${dayjs(reread.last_read_at).fromNow()}`;
  }
  return null;
}

interface PagesSectionProps {
  page: FestivalPageRead;
  form: PageForm;
  dispatch: (action: PageFormAction) => void;
  editable: boolean;
}

const withoutScheme = (url: string): string => url.replace(/^[a-z]+:\/\//i, "");

/** The pages Tina read for this festival, each with the box that says whether it is about it. */
export function PagesSection({
  page,
  form,
  dispatch,
  editable,
}: PagesSectionProps) {
  return (
    <section className="flex flex-col gap-3 rounded-lg border p-4">
      <h2 className="text-lg font-semibold">Pages Tina read</h2>
      <p className="text-sm text-muted-foreground">
        Untick a page that is not about this festival. Tina then publishes it as
        a normal event.
      </p>
      <ul className="flex flex-col gap-2">
        {page.pages.map((read) => {
          const choice = form.pages[read.url];
          if (!choice) return null;
          return (
            <li key={read.url} className="flex flex-wrap items-center gap-3">
              <Checkbox
                aria-label={`${read.url} is part of this festival`}
                checked={choice.member}
                disabled={!editable}
                onCheckedChange={(checked) =>
                  dispatch({
                    type: "set_member",
                    url: read.url,
                    member: checked === true,
                  })
                }
              />
              <a
                href={read.url}
                target="_blank"
                rel="noreferrer"
                className="text-sm underline"
              >
                {read.site} · {withoutScheme(read.url)}
              </a>
              <span className="text-xs text-muted-foreground">
                read {dayjs(read.read_at).format("D MMM")}
              </span>
              {rereadText(read.reread) ? (
                <span className="text-xs text-muted-foreground">
                  {rereadText(read.reread)}
                </span>
              ) : null}
              {choice.ownPage ? (
                <Badge variant="outline" className="font-normal">
                  Own page
                </Badge>
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
